import React, { useEffect, useMemo, useState } from 'react';
import CustomTable from '../../share-components/CustomTable';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PRODUCT_CREATE, ROUTE_PRODUCT_DETAILS, ROUTE_PRODUCT_EDIT, ROUTE_PRODUCT_LIST } from '../../constants/routeConstants';
import { Accordion, Button, Card } from 'react-bootstrap';
import { useAppContext } from '../../share-components/AppContext';
import { getActiveCategories, getActiveProducts, getInactiveProducts } from '../../services/ManagementService';
import NoDataFound from '../../share-components/NoDataFound';
import useScrollRestoration from '../../share-components/UseScrollRestoration';
import SearchColumnSelect from '../../share-components/SearchColumnSelect';
import FormTitle from '../../share-components/FormTitle';
import {
    currentMonth, currentYear, formatDateTime, getAvailableMonths, getChangingSelectColor, handleFromMonthChange,
    handleFromYearChange, handleToMonthChange, handleToYearChange, isNullOrEmptyString, years
} from '../../services/UtilityService';
import FloatingCartButton from '../../share-components/FloatingCartButton';
import noImage from '../../assets/No_Image_Available.jpg';
import { SELECT_ALL_DEFAULT, SERVER_ERROR_MESSAGE, TBL_DATA_SEPARATOR } from '../../constants/commonConstants';

const ProductListCommon = ({ isForPurchase = false, parentProductListShow, toggleProductListShow, selectedParentProduct, pageName,
    setParentSelectedProduct, setFirstLoadedTrigger, parentOriginalCart = [] }) => {

    const sessionStorageKey = ROUTE_PRODUCT_LIST;

    useScrollRestoration(pageName, !parentProductListShow);

    const productStatusOptions = {
        active: "Active",
        inactive: "Inactive"
    }

    const initialSearchDataObj = sessionStorage.getItem(sessionStorageKey);
    const initialSearchData = initialSearchDataObj && !isForPurchase ? JSON.parse(initialSearchDataObj) : null;

    const { loading, setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [productList, setProductList] = useState([]);
    const [productListTableData, setProductListTableData] = useState([]);
    const [productStatus, setProductStatus] = useState(initialSearchData?.productStatus || productStatusOptions.active);
    const [categoryId, setCategoryId] = useState(initialSearchData?.categoryId || "");
    const [categories, setCategories] = useState([]);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const [titleText, setTitleText] = useState(initialSearchData?.productStatus || productStatusOptions.active);

    const [fromYear, setFromYear] = useState(initialSearchData?.fromYear || currentYear);
    const [fromMonth, setFromMonth] = useState(initialSearchData?.fromMonth || currentMonth);
    const [toYear, setToYear] = useState(initialSearchData?.toYear || currentYear);
    const [toMonth, setToMonth] = useState(initialSearchData?.toMonth || currentMonth);
    const [filteredData, setFilteredData] = useState([]);
    const [searchData, setSearchData] = useState(null);

    const [afterGetDataStore, setAfterGetDataStore] = useState({
        fromYear: initialSearchData?.fromYear || currentYear,
        fromMonth: initialSearchData?.fromMonth || currentMonth,
        toYear: initialSearchData?.toYear || currentYear,
        toMonth: initialSearchData?.toMonth || currentMonth,
        productStatus: initialSearchData?.productStatus || productStatusOptions.active,
        categoryId: initialSearchData?.categoryId || ""
    });

    const navigate = useNavigate();

    useEffect(() => {
        if (isForPurchase) return;

        const searchData = {
            productStatus: productStatus,
            categoryId: categoryId,
            fromYear: fromYear,
            fromMonth: fromMonth,
            toYear: toYear,
            toMont: toMonth
        };
        setSearchData(searchData);
    }, [loading]);

    const fetchInitialLoad = async () => {
        try {
            setLoading(true);
            const categoryList = await getActiveCategories();
            let sortedCategories = (categoryList || [])?.sort((a, b) => (a.categoryName || "")?.localeCompare((b?.categoryName || "")));
            setCategories(sortedCategories);
            productStatus === productStatusOptions.inactive ? await fetchInActiveProducts() : await fetchActiveProducts();
            setIsFirstLoaded(true);
            if (setFirstLoadedTrigger) {
                setFirstLoadedTrigger(true);
            }
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    const fetchActiveProducts = async () => {
        setLoading(true);
        try {
            const response = await getActiveProducts(categoryId || '');
            const sortedProductList = (response || []).sort((a, b) => b.lastUpdatedDateTime.localeCompare(a.lastUpdatedDateTime));
            setProductList(sortedProductList);
            setTitleText(productStatusOptions.active);
            handelAfterGetProductData();
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchInActiveProducts = async () => {
        setLoading(true);
        try {
            const formData = {
                categoryId: categoryId || null,
                fromYear: fromYear,
                fromMonth: fromMonth,
                toYear: toYear,
                toMonth: toMonth
            }
            const response = await getInactiveProducts(formData);
            const sortedProductList = (response || []).sort((a, b) => b.lastUpdatedDateTime.localeCompare(a.lastUpdatedDateTime));
            setProductList(sortedProductList);
            setTitleText(productStatusOptions.inactive);
            handelAfterGetProductData();
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handelAfterGetProductData = () => {
        const afterClickGetDataStore = {
            fromYear,
            fromMonth,
            toYear,
            toMonth,
            productStatus,
            categoryId
        }
        setAfterGetDataStore(afterClickGetDataStore);
    }

    useEffect(() => {
        if (!isForPurchase) setRouteNaviData("PRODUCT >> PRODUCT LIST");
        fetchInitialLoad();
    }, []);

    useEffect(() => {
        const convertedResponse = (productList || []).map((pData) => ({
            ...pData,
            showData: getShowData(pData)
        }));
        setProductListTableData(convertedResponse);
    }, [productList]);

    useEffect(() => {
        if (setParentSelectedProduct && parentOriginalCart?.length > 0 && isFirstLoaded) {
            const updatedData = (parentOriginalCart || []).map(selectedProduct => {
                const allTargetItems = (productList || []).find(product => (parseInt(product.productId) || 0) === (parseInt(selectedProduct.productId) || 0))?.itemInfo || [];
                if (!allTargetItems || allTargetItems?.length <= 0) {
                    return {
                        ...selectedProduct,
                        isDeactivated: true
                    }
                }
                const editSelectedItems = selectedProduct.itemInfo || [];
                const updatedItemInfo = allTargetItems.map(item => {
                    const editItem = editSelectedItems.find(selectedItem => (parseInt(selectedItem.inventoryItemId) || 0) === (parseInt(item.inventoryItemId) || 0));
                    return editItem ? { ...item, purchasedAmount: editItem.purchasedAmount, quantity: editItem.quantity } : { ...item, purchasedAmount: null, quantity: null };
                });
                return {
                    ...selectedProduct,
                    itemInfo: updatedItemInfo,
                    isDeactivated: false
                }
            });
            setParentSelectedProduct(updatedData);
        }
    }, [parentOriginalCart, isFirstLoaded]);

    const getInStockColorSizeInfos = (productStockColorSizeInfos) => {
        const inStockColorSizeInfos = (productStockColorSizeInfos || []).filter(stock => (parseInt(stock.quantity) || 0) > 0);
        return inStockColorSizeInfos;
    }

    const getOutOfStockColorSizeInfos = (productStockColorSizeInfos) => {
        const outOfStockColorSizeInfos = (productStockColorSizeInfos || []).filter(stock => (parseInt(stock.quantity) || 0) < 0);
        return outOfStockColorSizeInfos;
    }

    const getShowData = (productData) => {
        // Generate plain text for searchability
        const isMultipleColorOrSize = productData?.colorCount > 0 || productData?.sizeCount > 0;
        const inStockColorSizeInfos = getInStockColorSizeInfos(productData?.productStockColorSizeInfos) || [];
        let inStockQty = isMultipleColorOrSize ?
            inStockColorSizeInfos?.reduce((sum, item) => sum + Math.abs(item.quantity), 0) :
            ((parseInt(productData?.totalPurchasedQuantity) || 0) - (parseInt(productData?.totalOrderedQuantity) || 0));
        inStockQty = inStockQty >= 0 ? inStockQty : 0;

        const outOfStockColorSizeInfos = getOutOfStockColorSizeInfos(productData?.productStockColorSizeInfos) || [];
        let outOfStockQty = isMultipleColorOrSize ?
            outOfStockColorSizeInfos?.reduce((sum, item) => sum + item.quantity, 0) :
            ((parseInt(productData?.totalPurchasedQuantity) || 0) - (parseInt(productData?.totalOrderedQuantity) || 0));
        outOfStockQty = outOfStockQty > 0 ? 0 : outOfStockQty;

        const plainText = [
            productData?.brandName || "",
            productData?.productName || "",
            productData?.categoryName || "",
            productData?.salePrice ? `${productData.salePrice}￥${productData.salePrice}¥` : "0￥0¥",
            productData?.colorCount > 0 ? `[${productData.colorCount} Color${productData?.colorCount > 1 ? 's' : ''}]` : "",
            productData?.sizeCount > 0 ? `[${productData.sizeCount} Size${productData?.sizeCount > 1 ? 's' : ''}]` : "",
            !isForPurchase ? productData?.lastUpdatedBy || "" : "",
            !isForPurchase ? formatDateTime(productData?.lastUpdatedDateTime) || "" : "",
            `InStock${inStockQty > 0 ? '(s)' : ''} (${inStockQty})`,
            outOfStockQty < 0 ? `OutOfStock (${outOfStockQty})` : ""
        ].filter(Boolean).join(TBL_DATA_SEPARATOR); // Join all values into a single string

        return {
            jsx: (
                <div style={{ fontWeight: '600' }}>
                    <div style={{
                        // fontWeight: '600',
                        display: 'flex',
                    }}>
                        <div style={{ paddingRight: '10px', flex: "1" }}>
                            <div><i className='bi bi-award'></i> {productData?.brandName}</div><hr className='m-0' />
                            <div><i className='bi bi-box'></i> {productData?.productName}</div><hr className='m-0' />
                            <div><i className='bi bi-tags'></i> {productData?.categoryName}</div><hr className='m-0' />
                        </div>
                        <div style={{ paddingTop: "5px" }}>
                            <img
                                src={productData?.productPicture ? `data:image/jpeg;base64,${productData?.productPicture}` : noImage}
                                alt="picture"
                                className="img-thumbnail"
                                style={{ width: "var(--orderProductPictureWidth)", height: "var(--orderProductPictureHeight)", objectFit: "cover" }}
                            />
                        </div>
                    </div>
                    <>
                        <div
                            style={{
                                color: `${(parseInt(productData?.salePrice) || 0) <= 0 ? 'red' : 'blue'}`
                            }}
                        ><i className='bi bi-cash'></i> {productData?.salePrice || 0}¥</div><hr className='m-0' />
                    </>
                    {isMultipleColorOrSize && (
                        <>
                            <div>
                                <i className='bi bi-palette'></i> {productData?.colorCount > 0 && <span>[{productData.colorCount} Color{productData?.colorCount > 1 ? 's' : ''}]</span>}
                                {productData?.colorCount > 0 && productData?.sizeCount > 0 ? ' ' : ''}
                                {productData?.sizeCount > 0 && <span>[{productData.sizeCount} Size{productData?.sizeCount > 1 ? 's' : ''}]</span>}
                            </div>
                            <hr className='m-0' />
                        </>
                    )}
                    {!isForPurchase && <><div><i className='bi bi-person-gear'></i> {productData?.lastUpdatedBy}</div><hr className='m-0' />
                        <div><i className='bi bi-alarm'></i> {formatDateTime(productData?.lastUpdatedDateTime)}</div><hr className='m-0' /></>}

                    {inStockQty > 0 && inStockColorSizeInfos.length > 0 ? <div className='stockAccordion'>
                        <Accordion>
                            <Accordion.Item eventKey="0" >
                                <Accordion.Header>
                                    <div>
                                        <i className='bi bi-bag-check'></i> InStock{inStockQty > 0 ? '(s)' : ''} ({inStockQty})
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div className='stockAndInStockDetailsBlock'>
                                        {inStockColorSizeInfos.map((stock, index) => (
                                            <span key={index}>{stock.color && `[${stock.color}]`}{stock.size && `[${stock.size}]`}<span style={{ color: 'blue' }}>({productData?.salePrice + stock.additionalSalePrice}¥)</span>(x{stock.quantity})<br /><hr className='m-0' /></span>
                                        ))}
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                        <hr className='m-0' />
                    </div> : inStockQty >= 0 && outOfStockQty >= 0 ? <>
                        <div>
                            <i className='bi bi-bag-check'></i> InStock{inStockQty > 0 ? '(s)' : ''} ({inStockQty})
                        </div><hr className='m-0' />
                    </> : <></>}

                    {outOfStockQty < 0 && outOfStockColorSizeInfos.length > 0 ? <div className='stockAccordion'>
                        <Accordion>
                            <Accordion.Item  eventKey="0" >
                                <Accordion.Header>
                                    <div style={{ color: "red" }}>
                                        <i className='bi bi-bag-dash'></i> OutOfStock ({outOfStockQty})
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div className='stockAndInStockDetailsBlock'>
                                        {outOfStockColorSizeInfos.map((stock, index) => (
                                            <span key={index}>{stock.color && `[${stock.color}]`}{stock.size && `[${stock.size}]`}<span style={{ color: 'blue' }}>({productData?.salePrice + stock.additionalSalePrice}¥)</span>(x{stock.quantity})<br /><hr className='m-0' /></span>
                                        ))}
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                        <hr className='m-0' />
                    </div> : outOfStockQty < 0 ? <div style={{ color: "red" }}>
                        <i className='bi bi-bag-dash'></i> OutOfStock ({outOfStockQty})
                    </div> : <></>}
                </div>
            ),
            text: plainText.toLowerCase() // Store searchable text
        };
    };

    const toggleItem = (array, item) => (
        array.some((product) => product.productId === item.productId) ? array.filter(p => p.productId !== item.productId) : [...array, item]
    );

    const getUniqueByKey = (data, key, mapFields) => (
        data.filter(item => !isNullOrEmptyString(item[key]))
            .filter((item, index, self) =>
                index === self.findIndex(i => i[key] === item[key])
            )
            .map(mapFields));



    const handleToggleAddToPurchase = (productData) => {
        if (!isForPurchase) return;

        const colors = getUniqueByKey(productData.itemInfo, 'colorId', item => ({
            colorId: item.colorId,
            color: item.color
        }));

        const sizes = getUniqueByKey(productData.itemInfo, 'sizeId', item => ({
            sizeId: item.sizeId,
            size: item.size
        }));

        const itemInfo = productData.itemInfo.map((item) => ({
            ...item,
            quantity: null,
            purchasedAmount: null
        }));

        setParentSelectedProduct(prev => {
            let productDataForPurchase = {
                productId: productData.productId,
                productName: productData.productName,
                brandName: productData.brandName,
                categoryName: productData.categoryName,
                productPicture: productData.productPicture,
                itemInfo: itemInfo,
                selectedColorId: null,
                selectedSizeId: null,
                colors: colors,
                sizes: sizes,
                isValidForm: true,
                quantity: null,
                purchasedAmount: null
            }
            return toggleItem(prev, productDataForPurchase)
        });
    };

    const filteredColumns = useMemo(() => {
        const columns = [
            {
                Header: 'Product Infos',
                accessor: 'showData',
                disableSortBy: true,
                style: {
                    minWidth: "150px",
                    maxWidth: "350px",
                },
                Cell: ({ row }) => row.original.showData.jsx,
            },
            {
                Header: 'Purchase',
                accessor: 'purchase',
                unSearchable: true,
                disableSortBy: true,
                headerClassName: 'disableSort text-center',
                className: 'text-center align-middle',
                style: {
                    minWidth: "70px",
                    // maxWidth: "100px"
                },
                Cell: ({ row }) => {
                    const isSelectedToPurchase = (selectedParentProduct || []).some((product) => product.productId === row.original.productId); //!!((selectedPurchaseProduct || []).find((selectedProductId) => selectedProductId === row.oroginal.productId));
                    return (
                        <div className='center-block'>
                            <Button
                                style={{
                                    minWidth: '60px'
                                }}
                                variant={`${isSelectedToPurchase ? "danger" : "warning"}`}
                                className="btn-sm icon-btn-vertical"
                                onClick={() => handleToggleAddToPurchase(row.original)}
                            >
                                <i className={`bi ${isSelectedToPurchase ? "bi-bag-dash" : "bi-bag-plus"}`}></i>
                                <span>{isSelectedToPurchase ? "Remove" : "Add"}</span>
                            </Button>
                        </div>
                    )
                },
            },
            {
                Header: 'Edit',
                accessor: 'edit',
                unSearchable: true,
                disableSortBy: true,
                headerClassName: 'disableSort text-center',
                className: 'text-center align-middle',
                style: {
                    minWidth: "70px",
                    // maxWidth: "100px"
                },
                Cell: ({ row }) => (
                    <div className='center-block'>
                        <Button
                            variant="secondary"
                            className="btn-sm icon-btn-vertical"
                            onClick={() => navigate(`${ROUTE_PRODUCT_EDIT}/${row.original.productId}`)}
                        >
                            <i className="bi bi-pencil-square"></i>
                            <span>Edit</span>
                        </Button>
                    </div>
                ),
            },
            {
                Header: 'Details',
                accessor: 'details',
                unSearchable: true,
                disableSortBy: true,
                headerClassName: 'disableSort text-center',
                className: 'text-center align-middle',
                style: {
                    minWidth: "70px",
                    // maxWidth: "100px"
                },
                Cell: ({ row }) => (
                    <div className='center-block'>
                        <Button
                            variant="secondary"
                            className="btn-sm icon-btn-vertical"
                            onClick={() => navigate(`${ROUTE_PRODUCT_DETAILS}/${row.original.productId}`)}
                        >
                            <i className="bi bi-info-circle"></i>
                            <span>Details</span>
                        </Button>
                    </div>
                ),
            }
        ];

        let filteredColumns = columns;


        if (isForPurchase) {
            filteredColumns = columns.filter(
                c => c.accessor !== 'details' && c.accessor !== 'edit'
            );
        } else if (titleText === productStatusOptions.inactive) {
            filteredColumns = columns.filter(
                c => c.accessor !== 'purchase' && c.accessor !== 'edit'
            );
        } else {
            filteredColumns = columns.filter(
                c => c.accessor !== 'purchase'
            );
        }

        return filteredColumns;
    }, [titleText, selectedParentProduct, isForPurchase]);



    const goToCreateProduct = () => {
        navigate(ROUTE_PRODUCT_CREATE);
    }

    const handleGetData = async () => {
        if (productStatus === productStatusOptions.active) {
            await fetchActiveProducts();
        } else {
            await fetchInActiveProducts();
        }
    }

    return (
        <div id='productListPage'>
            {!isForPurchase && <>
                <FormTitle title={`${titleText} Product List`} isCenter={true} />
                {isFirstLoaded && <>
                    {afterGetDataStore?.productStatus === productStatusOptions.inactive && <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
                        {afterGetDataStore?.fromYear}/{afterGetDataStore?.fromMonth} ～ {afterGetDataStore?.toYear}/{afterGetDataStore?.toMonth}
                    </div>}
                </>}
            </>}
            {isFirstLoaded && <>
                <div style={{ overflow: "auto", paddingBottom: "50px" }}>
                    <div style={{ minWidth: "max-content", maxWidth: "650px", margin: '0 auto' }}>
                        <Card>
                            <Card.Body style={{ padding: '10px 5px 20px 5px' }}>
                                <div style={{}}>
                                    <div>
                                        <div className="mb-1" style={{ display: "flex", gap: "10px", justifyContent: "start", alignItems: "end", flexWrap: "wrap" }}>
                                            {/* Active */}
                                            {!isForPurchase && <div className="d-inline">
                                                <label className="me-1 custom-label" htmlFor="active" style={{ minWidth: "50px" }}>
                                                    Active/Inactive
                                                </label>
                                                <div>
                                                    <select
                                                        id="active"
                                                        className={`customInputControl me-2 select-min-max-width ${getChangingSelectColor(productStatus, afterGetDataStore?.productStatus)}`}
                                                        value={productStatus}
                                                        onChange={(e) => setProductStatus(e.target.value)}
                                                    >
                                                        {Object.entries(productStatusOptions).map(([key, value]) => (
                                                            <option key={key} value={value}>{value} Products</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>}

                                            {/* Category */}
                                            <div className="d-inline">
                                                <label className="me-1 custom-label" htmlFor="category" style={{ minWidth: "50px" }}>
                                                    Category
                                                </label>
                                                <div>
                                                    <select
                                                        id="category"
                                                        className={`customInputControl me-2 select-min-max-width ${getChangingSelectColor(categoryId, afterGetDataStore?.categoryId)}`}
                                                        value={categoryId}
                                                        onChange={(e) => setCategoryId(e.target.value || '')}
                                                    >
                                                        <option value="">{SELECT_ALL_DEFAULT}</option>
                                                        {categories.map(category => (
                                                            <option key={category.categoryId} value={category.categoryId}>{category.categoryName}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                        </div>
                                        {productStatus !== productStatusOptions.active && <div className="mt-1 mb-1" style={{ display: "flex", gap: "10px", justifyContent: "start", alignItems: "end", flexWrap: "wrap" }}>
                                            {/* From Year & Month */}
                                            <div className="d-inline">
                                                <label className="me-1 custom-label" htmlFor="fromYear" style={{ minWidth: "50px" }}>
                                                    From
                                                </label>
                                                <div>
                                                    <select
                                                        id="fromYear"
                                                        className={`customInputControl ${getChangingSelectColor(fromYear, afterGetDataStore?.fromYear)}`}
                                                        value={fromYear}
                                                        onChange={(e) => handleFromYearChange(e, fromMonth, toYear, toMonth, setFromYear, setFromMonth, setToYear, setToMonth)}
                                                    >
                                                        {years.map((year) => (
                                                            <option key={year} value={year}>{year}</option>
                                                        ))}
                                                    </select>
                                                    <span className="separator">/</span>
                                                    <select
                                                        id="fromMonth"
                                                        className={`customInputControl ${getChangingSelectColor(fromMonth, afterGetDataStore?.fromMonth)}`}
                                                        value={fromMonth}
                                                        onChange={(e) => handleFromMonthChange(e, fromYear, toYear, toMonth, setFromMonth, setToMonth)}
                                                    >
                                                        {getAvailableMonths(fromYear).map((month) => (
                                                            <option key={month} value={month}>
                                                                {month.toString().padStart(2, "0")}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            {/* To Year & Month */}
                                            <div className="d-inline">
                                                <label className="me-1 custom-label" htmlFor="toYear" style={{ minWidth: "50px" }}>
                                                    To
                                                </label>
                                                <div>
                                                    <select
                                                        id="toYear"
                                                        className={`customInputControl ${getChangingSelectColor(toYear, afterGetDataStore?.toYear)}`}
                                                        value={toYear}
                                                        onChange={(e) => handleToYearChange(e, fromYear, fromMonth, toMonth, setFromYear, setToYear, setFromMonth)}
                                                    >
                                                        {years.map((year) => (
                                                            <option key={year} value={year}>{year}</option>
                                                        ))}
                                                    </select>
                                                    <span className="separator">/</span>
                                                    <select
                                                        id="toMonth"
                                                        className={`customInputControl ${getChangingSelectColor(toMonth, afterGetDataStore?.toMonth)}`}
                                                        value={toMonth}
                                                        onChange={(e) => handleToMonthChange(e, fromYear, fromMonth, toYear, setFromMonth, setToMonth)}
                                                    >
                                                        {getAvailableMonths(toYear).map((month) => (
                                                            <option key={month} value={month}>
                                                                {month.toString().padStart(2, "0")}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                        </div>}
                                        <div className="d-inline">
                                            <Button type='button' variant='success' className="btn-sm appBtn"
                                                onClick={handleGetData}
                                            >Get Data</Button>
                                        </div>
                                        <div style={{ marginBottom: '10px', marginTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'end', flexWrap: 'wrap', gap: '5px' }}>
                                            <SearchColumnSelect data={productListTableData} setData={setFilteredData} columns={filteredColumns}
                                                isNoColumn={true} storeSessionKey={!isForPurchase ? sessionStorageKey : null} sessionData={!isForPurchase ? searchData : null}
                                            />
                                            {/* {!isForPurchase && <div style={{ display: 'inline-block' }}>
                                            <Button type='button' variant='warning' className='btn-sm' onClick={goToCreateProduct}><i className="bi bi-plus-circle"></i> Add New Product</Button>
                                        </div>} */}


                                        </div>
                                        {filteredData?.length > 0 ?
                                            <CustomTable
                                                tableId='productListTbl'
                                                columns={filteredColumns}
                                                tblData={filteredData || []}
                                                showPagination={(filteredData || []).length > 0}
                                                itemsPerPage={10}
                                                // initialSortBy={[{ id: 'showData', desc: false }]}
                                                noHeader={true}
                                                isStorePageData={true}
                                            /> :
                                            <NoDataFound />}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>
                {(isForPurchase && parentProductListShow) && <FloatingCartButton
                    cartCount={selectedParentProduct.length || 0}
                    clickCallBack={toggleProductListShow}
                />}
            </>}
        </div>
    );
};

export default ProductListCommon;